import React, { useState, useEffect, FormEvent, ChangeEvent } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./resetPassword.less";
import FlyoutLayout from "../../shared/FlyoutLayout/FlyoutLayout";
import service from "../../services/service";

interface ResetPasswordProps {
  isOpen: boolean;
  closeModal: () => void;
  onBackToLogin: () => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({
  isOpen,
  closeModal,
  onBackToLogin,
}) => {
  const displayName = "cmp-resetpassword";
  const lockLogo = new URL("../../assets/lock.svg", import.meta.url).href;

  const { token } = useParams<{ token: string }>();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      setTokenValid(false);
      setIsValidating(false);
      return;
    }
    setTokenValid(true);
    setIsValidating(false);
  }, [token]);

  if (!isOpen) return null;

  const validatePassword = (password: string): string | null => {
    if (!password.trim()) {
      return "New password cannot be empty";
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumbers) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }

    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError(
        "Invalid or expired reset link. Please request a new password reset."
      );
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!confirmPassword.trim()) {
      setError("Please confirm your password");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setDisableBtn(true);

    try {
      const response = await service.resetPassword(token, newPassword);

      if (response.message) {
        toast.success(
          "Password reset successful! You can now login with your new password."
        );
        setSuccess(true);

        setTimeout(() => {
          onBackToLogin();
        }, 3000);
      }
    } catch (err: any) {
      console.error("Password reset failed:", err);

      let errorMessage = "Something went wrong. Please try again.";

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.status === 401) {
        errorMessage =
          "Invalid or expired reset token. Please request a new password reset.";
      } else if (err?.response?.status === 404) {
        errorMessage = "User not found. Please contact support.";
      } else if (err?.response?.status === 400) {
        errorMessage =
          "Invalid or expired reset token. Please request a new password reset.";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);

      if (errorMessage.includes("Invalid or expired")) {
        setTokenValid(false);
      }
    } finally {
      setDisableBtn(false);
    }
  };

  const handleBackToLogin = () => {
    onBackToLogin();
  };

  if (isValidating) {
    return (
      <FlyoutLayout
        displayName={displayName}
        isOpen={isOpen}
        closeModal={closeModal}
        contentPadding="100px 32px"
        contentAlign="center"
        contentJustify="center"
      >
        <div className={`${displayName}__loading`}>
          <div className={`${displayName}__spinner`}></div>
          <p>Validating reset link...</p>
        </div>
      </FlyoutLayout>
    );
  }

  if (tokenValid === false) {
    return (
      <FlyoutLayout
        displayName={displayName}
        isOpen={isOpen}
        closeModal={closeModal}
        contentPadding="100px 32px"
        contentAlign="center"
        contentJustify="center"
      >
        <div className={`${displayName}__error-state`}>
          <div className={`${displayName}__error-icon`}>
            <svg
              width="60"
              height="60"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2" />
              <path
                d="m15 9-6 6M9 9l6 6"
                stroke="#EF4444"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h2 className={`${displayName}__title ${displayName}__title--error`}>
            Invalid Reset Link
          </h2>
          <p className={`${displayName}__error-message`}>
            This password reset link is invalid or has expired. Please request a
            new password reset from the login page.
          </p>
          <div className={`${displayName}__submit-btn`}>
            <button
              className={`${displayName}__submit`}
              onClick={handleBackToLogin}
            >
              Go to Login
            </button>
          </div>
        </div>
      </FlyoutLayout>
    );
  }

  return (
    <FlyoutLayout
      displayName={displayName}
      isOpen={isOpen}
      closeModal={closeModal}
      contentPadding="100px 32px"
      contentAlign="center"
      contentJustify="center"
    >
      {success ? (
        <>
          <div className={`${displayName}__success-container`}>
            <div className={`${displayName}__success-icon`}>
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#10B981"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="m9 12 2 2 4-4"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className={`${displayName}__header`}>
              <h2
                className={`${displayName}__title ${displayName}__title--success`}
              >
                Password Reset Successful!
              </h2>
              <div className={`${displayName}__success-message`}>
                Your password has been reset successfully. You can now login
                with your new password.
              </div>
              <p className={`${displayName}__redirect-message`}>
                Redirecting to login page in 3 seconds...
              </p>
            </div>

            <div className={`${displayName}__submit-btn`}>
              <button
                className={`${displayName}__submit`}
                onClick={handleBackToLogin}
              >
                Go to Login Now
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={`${displayName}__header`}>
            <h2 className={`${displayName}__title`}>
              <span className={`${displayName}__title--italic`}>
                Almost there
              </span>{" "}
              — <br />
              create your new password
            </h2>
          </div>

          <form
            className={`${displayName}__form`}
            onSubmit={handleSubmit}
            noValidate
          >
            {/* New Password */}
            <div
              className={`${displayName}__field ${
                error && error.includes("New password")
                  ? `${displayName}__field--shake ${displayName}__field--error`
                  : ""
              }`}
            >
              <label className={`${displayName}__label`} htmlFor="newPassword">
                New Password
              </label>
              <div className={`${displayName}__input-area`}>
                <img
                  src={lockLogo}
                  alt="Password"
                  className={`${displayName}__input-icon`}
                />
                <input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  className={`${displayName}__input`}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNewPassword(e.target.value)
                  }
                  disabled={disableBtn}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={`${displayName}__toggle-password`}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label="Toggle password visibility"
                >
                  {showNewPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div
              className={`${displayName}__field ${
                error && (error.includes("confirm") || error.includes("match"))
                  ? `${displayName}__field--shake ${displayName}__field--error`
                  : ""
              }`}
            >
              <label
                className={`${displayName}__label`}
                htmlFor="confirmPassword"
              >
                Confirm Password
              </label>
              <div className={`${displayName}__input-area`}>
                <img
                  src={lockLogo}
                  alt="Password"
                  className={`${displayName}__input-icon`}
                />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`${displayName}__input`}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(e.target.value)
                  }
                  disabled={disableBtn}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={`${displayName}__toggle-password`}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label="Toggle password visibility"
                >
                  {showConfirmPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>

            {error && (
              <div className={`${displayName}__error-txt`} role="alert">
                {error}
              </div>
            )}

            {/* Submit */}
            <div className={`${displayName}__submit-btn`}>
              <button
                className={`${displayName}__submit ${
                  disableBtn ? `${displayName}__submit--loading` : ""
                }`}
                type="submit"
                disabled={disableBtn}
              >
                {disableBtn ? "Resetting Password..." : "Reset Password"}
              </button>
            </div>
          </form>

          <div className={`${displayName}__back-section`}>
            <button
              type="button"
              className={`${displayName}__back-link`}
              onClick={handleBackToLogin}
            >
              ← Back to Login
            </button>
          </div>
        </>
      )}
    </FlyoutLayout>
  );
};

export default ResetPassword;
